import { useGlobalWebcast, useGetWebcast } from "JS/React/Hooks/Webcast";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { AppHeader } from "JS/React/Components/AppHeader";
import { Layout } from "../Layout";
import { AppTypography } from "JS/React/Components/AppTypography";
import { Grid, Box, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppButton } from "JS/React/Components/AppButton";
import { webcastFirebaseService } from "JS/Services/Firebase/WebcastService";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import qs from "qs";
export interface WebcastProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

const StyledLayout = styled(Grid)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyItems: "center",
    justifyContent: "flex-start",
    height: `calc(100vh - ${theme.footer.height} - ${theme.header.height})`,
}));

const StyledHeader = styled(AppTypography)(({ theme }) => ({
    textAlign: "center",
    color: theme.palette.primary.main,
}));

const StyledText = styled(AppTypography)(({ theme }) => ({
    textAlign: "center",
    color: theme.palette.common.black,
}));

const StyledSubtitle = styled(AppTypography)(({ theme }) => ({
    textAlign: "center",
    color: theme.colors.webcastSubtitle,
    fontWeight: "bold",
    textTransform: "uppercase",
}));

export const WebcastDetail = (props: WebcastProps) => {
    const params: any = useParams();
    const { webcasts } = useGlobalWebcast();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { title } = params;
    const { loading } = useGetWebcast(webcasts && webcasts.length > 0);
    const [isAuthorized, setAuthorized] = useState(true);
    const [isStarted, setStarted] = useState(true);
    const [isEnded, setEnded] = useState(false);
    const [remaining, setRemaining] = useState<number>(undefined);

    const { queryParams, handleBackNavigation } = useHandler();

    useEffect(() => {
        webcastFirebaseService.writeOpenDate(title);
    }, [title]);

    const { enqueueSnackbar } = useSnackbar();

    const webcast = useMemo(() => {
        const w = webcasts?.find((w) => w.Description === title);
        if (w) {
            setAuthorized(w.isAuthorized);
            const startDate = new Date(w.StartDate);
            const endDate = new Date(w.EndDate);
            const hasStarted =
                Date.now() >= startDate.getTime() &&
                Date.now() < endDate.getTime();
            setStarted(hasStarted);
            setEnded(Date.now() > endDate.getTime());
            return w;
        } else setAuthorized(false);
    }, [title, webcasts]);

    useEffect(() => {
        let interval = null;
        if (!isStarted && webcast) {
            interval = setInterval(() => {
                const startDate = new Date(webcast.StartDate);
                const diff = startDate.getTime() - Date.now();
                if (diff === 0) setStarted(true);
                setRemaining(diff);
            }, 1000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isStarted, webcast]);

    const openWebcast = () => {
        if (isStarted) {
            webcastFirebaseService.writeStartDate(title);
            navigate(
                linkProvider.react
                    .webcast()
                    .stream(
                        title,
                        queryParams?.backNav === "true" ? true : false,
                    ),
            );
        } else {
            enqueueSnackbar("Your event has not started yet.", {
                variant: "warning",
            });
        }
    };

    const timerText = () => {
        const totalSeconds = remaining / 1000;
        const seconds = (totalSeconds % 60).toFixed(0);
        const minutes = (Math.floor(totalSeconds / 60) % 60).toFixed(0);
        const hours = (Math.floor(totalSeconds / (60 * 60)) % 24).toFixed(0);
        const days = Math.floor(totalSeconds / 86400).toFixed(0);
        return `${days}D:${hours}H:${minutes}M:${seconds}S`;
    };

    return (
        <Layout>
            <AppHeader
                title={"Webcast"}
                canGoBack
                searchable={false}
                onBackClick={handleBackNavigation}
            />
            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={"Fetching Webcast"}
                />
            )}
            {webcast && (
                <StyledLayout item xs={12} md={12}>
                    <StyledHeader variant="h6">
                        {webcast.Description}
                    </StyledHeader>
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <StyledSubtitle variant="body1">
                        {!isAuthorized
                            ? `You are not authorized to access this webcast.`
                            : isEnded
                            ? `There is no scheduled webcast at this time.`
                            : `You are authorized to access this webcast.`}
                    </StyledSubtitle>
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    {isStarted && isAuthorized && (
                        <Box>
                            {!!webcast.WebcastPassword && (
                                <StyledText variant="body1">
                                    <b>
                                        The password for this event is:
                                        <br />
                                        <br />
                                        {webcast.WebcastPassword}
                                    </b>
                                </StyledText>
                            )}
                            <StyledText variant="body1">
                                <br />
                                <br />
                                To access the webcast,
                                <br />
                                Press the "Start Streaming" button below.
                            </StyledText>
                            <br />
                            <br />
                            <Box display={"flex"} justifyContent={"center"}>
                                <AppButton
                                    onClick={openWebcast}
                                    style={{ height: "40px" }}
                                    rounded
                                    buttonVariant="grey-outlined"
                                >
                                    Start Streaming
                                </AppButton>
                            </Box>
                        </Box>
                    )}
                    {!isStarted && remaining && (
                        <>
                            <StyledText>
                                Your event will start in:
                            </StyledText>
                            <StyledText>
                                {timerText()}
                            </StyledText>
                        </>
                    )}
                </StyledLayout>
            )}
        </Layout>
    );
};

const useHandler = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { homeStack } = useGlobalNavStack();
    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { backNav = "" } = parsedQueryString;

        return {
            backNav: backNav.toString(),
        };
    }, [location?.search]);

    const handleBackNavigation = () => {
        if (queryParams?.backNav === "true") {
            navigate(-1);
        } else {
            if (!homeStack?.isHomeTrack) {
                navigate(linkProvider.react.webcast().index());
            } else {
                navigate(-1);
            }
        }
    };
    return { queryParams, handleBackNavigation };
};
